import { useEffect, useState } from 'react';
import './ProgramPages.css';
import {
  IconActivity,
  IconAtom,
  IconBook,
  IconCalculator,
  IconCheck,
  IconDot,
  IconGlobe,
  IconHeart,
  IconLanguage,
  IconMonitor,
  IconPalette,
  IconTarget,
} from './ProgramIcons';
import heroImage1 from './assets/news/news7.jpg';
import heroImage2 from './assets/news/news8.jpg';
import heroImage3 from './assets/news/news1.jpg';

function PrimaryPage({ onNavigate }) {
  const [activeHero, setActiveHero] = useState(0);
  const heroSlides = [heroImage1, heroImage2, heroImage3];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  const programHighlights = [
    {
      icon: IconBook,
      title: 'Core Academics',
      description: 'Strong foundations in English, Math, Science, and Bahasa Indonesia aligned with national and Cambridge standards.',
    },
    {
      icon: IconGlobe,
      title: 'Bilingual Learning',
      description: 'English and Bahasa Indonesia instruction that builds fluency and cultural understanding.',
    },
    {
      icon: IconAtom,
      title: 'STEM Focus',
      description: 'Hands-on science, technology, engineering, and math projects that develop critical thinking.',
    },
    {
      icon: IconTarget,
      title: 'Character & Leadership',
      description: 'Faith-based values, responsibility, and leadership skills woven into daily learning.',
    },
  ];

  const gradePrograms = [
    {
      grade: 'Grade 1-2',
      title: 'Foundation Years',
      focus: 'Building literacy, numeracy, and learning habits',
      highlights: [
        'Phonics and reading fluency',
        'Addition, subtraction, and place value',
        'Science exploration and observation',
        'Social skills and classroom routines',
      ],
    },
    {
      grade: 'Grade 3-4',
      title: 'Development Years',
      focus: 'Expanding knowledge and independent learning',
      highlights: [
        'Reading comprehension and writing',
        'Multiplication, division, and fractions',
        'Scientific inquiry and experiments',
        'Project-based learning',
      ],
    },
    {
      grade: 'Grade 5-6',
      title: 'Mastery Years',
      focus: 'Deepening understanding and preparing for secondary',
      highlights: [
        'Advanced literacy and research skills',
        'Complex problem-solving in math',
        'Integrated STEM projects',
        'Leadership and service learning',
      ],
    },
  ];

  const subjects = [
    { name: 'English Language Arts', icon: IconBook },
    { name: 'Mathematics', icon: IconCalculator },
    { name: 'Science', icon: IconAtom },
    { name: 'Bahasa Indonesia', icon: IconLanguage },
    { name: 'Social Studies', icon: IconGlobe },
    { name: 'ICT & Digital Literacy', icon: IconMonitor },
    { name: 'Arts & Music', icon: IconPalette },
    { name: 'Physical Education', icon: IconActivity },
    { name: 'Character Education', icon: IconHeart },
  ];

  const learningFeatures = [
    {
      title: 'Project-Based Learning',
      description: 'Students work on real-world projects that integrate multiple subjects and develop problem-solving skills.',
      image: heroImage1,
    },
    {
      title: 'Differentiated Instruction',
      description: 'Teaching adapted to meet individual learning needs, ensuring every student is challenged and supported.',
      image: heroImage2,
    },
    {
      title: 'Technology Integration',
      description: 'Digital tools and resources that enhance learning and prepare students for the modern world.',
      image: heroImage3,
    },
    {
      title: 'Assessment for Learning',
      description: 'Regular feedback, formative assessments, and progress tracking to guide student growth.',
      image: heroImage1,
    },
  ];

  const extracurricular = [
    'Sports Teams (Soccer, Basketball, Swimming)',
    'Arts & Music Clubs',
    'STEM & Robotics',
    'Language Clubs',
    'Student Council',
    'Community Service Projects',
  ];

  return (
    <div className="program-page primary-page" id="primary">
      {/* Hero Section */}
      <section className="program-hero">
        <div className="program-hero-media">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`program-hero-slide ${index === activeHero ? 'active' : ''}`}
            >
              <img src={slide} alt="Primary Program" />
            </div>
          ))}
        </div>
        <div className="program-hero-overlay" />
        <div className="program-hero-content">
          <span className="program-hero-badge">Grades 1-6</span>
          <h1>Primary Program</h1>
          <p className="program-hero-subtitle">
            Building academic excellence, character, and confidence for lifelong success
          </p>
          <div className="program-hero-stats">
            <div className="program-stat">
              <span className="stat-number">24</span>
              <span className="stat-label">Max Class Size</span>
            </div>
            <div className="program-stat">
              <span className="stat-number">6</span>
              <span className="stat-label">Grade Levels</span>
            </div>
            <div className="program-stat">
              <span className="stat-number">9</span>
              <span className="stat-label">Core Subjects</span>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="program-overview">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Program Overview</span>
            <h2>Excellence in Primary Education</h2>
            <p>
              Our primary program provides a rigorous, well-rounded education that develops 
              academic skills, critical thinking, and strong character. We prepare students 
              for secondary school and beyond through engaging, purposeful learning.
            </p>
          </div>

          <div className="program-highlights-grid">
            {programHighlights.map((item, index) => {
              const Icon = item.icon;
              return (
              <div className="highlight-card" key={index}>
                <div className="highlight-icon" aria-hidden="true">
                  <Icon />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grade Programs */}
      <section className="grade-programs">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Grade Levels</span>
            <h2>Learning Journey Through Primary</h2>
            <p>Each stage builds on the previous, creating a seamless path to mastery</p>
          </div>

          <div className="grade-programs-grid">
            {gradePrograms.map((program, index) => (
              <div className="grade-program-card" key={index}>
                <div className="grade-badge">{program.grade}</div>
                <h3>{program.title}</h3>
                <p className="grade-focus">{program.focus}</p>
                <ul className="grade-highlights">
                  {program.highlights.map((highlight, idx) => (
                    <li key={idx}>
                      <span className="bullet" aria-hidden="true">
                        <IconDot />
                      </span>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subjects */}
      <section className="subjects-section">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Curriculum</span>
            <h2>Core Subjects</h2>
            <p>A balanced curriculum that develops the whole child</p>
          </div>

          <div className="subjects-grid">
            {subjects.map((subject, index) => {
              const Icon = subject.icon;
              return (
                <div className="subject-chip" key={index}>
                  <span className="subject-icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="subject-name">{subject.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Learning Features */}
      <section className="learning-areas">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Our Approach</span>
            <h2>How We Teach</h2>
            <p>Innovative methods that engage and inspire young learners</p>
          </div>

          <div className="learning-areas-grid">
            {learningFeatures.map((feature, index) => (
              <div className="learning-area-card" key={index}>
                <div className="learning-area-image">
                  <img src={feature.image} alt={feature.title} />
                </div>
                <div className="learning-area-content">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extracurricular */}
      <section className="extracurricular-section">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Beyond the Classroom</span>
            <h2>Extracurricular Activities</h2>
            <p>Opportunities to explore interests, develop talents, and build friendships</p>
          </div>

          <div className="extracurricular-grid">
            {extracurricular.map((activity, index) => (
              <div className="activity-card" key={index}>
                <span className="activity-icon" aria-hidden="true">
                  <IconCheck />
                </span>
                <span className="activity-name">{activity}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="campus-link">
        <div className="program-container">
          <div className="campus-link-card">
            <div>
              <p className="campus-link-kicker">School Center</p>
              <h3>School center information</h3>
              <p>
                This program is part of our main campus network. Visit the central school site for
                official information and updates.
              </p>
            </div>
            <a
              className="campus-link-action"
              href="https://kinderfieldhighfield.sch.id/simprug"
              target="_blank"
              rel="noreferrer"
            >
              Open main campus site
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="parent-info">
        <div className="program-container">
          <div className="cta-section">
            <h3>Ready to Join Our Primary Program?</h3>
            <p>Discover how we can support your child's academic and personal growth</p>
            <div className="cta-buttons">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onNavigate && onNavigate('register')}
              >
                Apply Now
              </button>
              <a href="/contact" className="btn btn-secondary">Schedule a Tour</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PrimaryPage;
